/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { useAdminGetUsers, useAdminUpdateUser, useAdminDeleteUser } from "@/services/userService";
import { ToggleLeft, ToggleRight, Pencil, Plus, Search, Trash, SearchX, Circle, Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { ScaleLoader } from "react-spinners";
import { Link } from "@/router";
import { useAdminGetRolesDefinitionsSWR } from "@/services/roleService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";
import { useAdminGetRegionsSWR } from "@/services/regionService";
import { objectToQueryString } from "@/utils/objectToQueryString";
import { queryStringToObject } from "@/utils/queryStringToObject";


/* DayJs */
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import localizedFormat from "dayjs/plugin/localizedFormat"
import utc from "dayjs/plugin/utc"

import "dayjs/locale/fr"

dayjs.locale("fr")
dayjs.extend(relativeTime)
dayjs.extend(localizedFormat)
dayjs.extend(utc)


const initialState = {
  errorMessage: "",
  isLoading: false,
  filters: {
    id: "",
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    enabled: "",
    role_type: "",
    skill_id: "",
    competition_id: "",
    region_id: "",
    last_seen_before: "",
    last_seen_after: "",
    on_keycloak: "",
    on_inscriptions_plateform: "",
    hidden: "",
    skip: "",
    limit: "",
  },
  users: null,
  page_size: 10,
  page: 1,
  total: 0
};



const reducer = (state, action) => {
  switch (action.type) {
    case "setErrorMessage":
      return { ...state, errorMessage: action.payload };
    case "setIsLoading":
      return { ...state, isLoading: action.payload };
    case "setFilter":
      return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } };
    case "setFilters":
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case "setUsers":
      return { ...state, users: action.payload };
    case "setPage":
      return { ...state, page: action.payload }
    case "setPageSize":
      return { ...state, page_size: action.payload }
    case "setTotal":
      return { ...state, total: action.payload }
    default:
      console.error("Unknown action.type " + action.type);
      throw new Error();
  }
};



const ACTIONS = {
  SET_ERROR_MESSAGE: "setErrorMessage",
  SET_IS_LOADING: "setIsLoading",
  SET_FILTER: "setFilter",
  SET_FILTERS: "setFilters",
  SET_USERS: "setUsers",
  SET_PAGE: "setPage",
  SET_PAGE_SIZE: "setPageSize",
  SET_TOTAL: "setTotal",
};



export default function Index() {
  const [ , setPageTitle] = useOutletContext();
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const adminGetUsers = useAdminGetUsers();
  const {
    data: rolesDefinitions,
    error: errorRolesDefinitions,
    isLoading: isLoadingRolesDefinitions,
    isValidating: isValidatingRolesDefinitions,
  } = useAdminGetRolesDefinitionsSWR();

  const {data: regions } = useAdminGetRegionsSWR()

  const {data: skills } = useAdminGetSkillsSWR()
  const {data: competitions } = useAdminGetCompetitionsSWR()

  const adminUpdateUser = useAdminUpdateUser();
  const adminDeleteUser = useAdminDeleteUser();


  /* On met à jour le titre */
  useEffect(() => {
    setPageTitle("Gestion des utilisateurs");

    return () => {
      setPageTitle("Titre")
    }
  }, [setPageTitle]);



  /* On récupère les filtres de recherche si ils sont présents dans l'URL */
  useEffect(() => {
    if (searchParams) {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: queryStringToObject(searchParams.toString()) });
    }
  }, [searchParams])



  /* On met à jour l'URL quand les filtres changent */
  useEffect(() => {
    if(JSON.stringify(state.filters) !== JSON.stringify(searchParams)) {
      setSearchParams(objectToQueryString(state.filters));
    }
  }, [state.filters])



  /* Lorsque la page charge, si les parametres sont vides, on set skip et limit */
  useEffect(() => {
    if (searchParams.size == 0) {
      setSearchParams("limit=10&skip=0")
    }
  }, [])


  
  /* Lorsque l'on modifie la taille de la page on rafraichit */
  useEffect(() => {
      if(state?.page_size)
          dispatch({ type: ACTIONS.SET_PAGE, payload: 1 })
          dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
          dispatch({ type: ACTIONS.SET_FILTER, subtype: "limit", payload: String(state.page_size) })
  }, [state.page_size])



  /* Lorsque l'on modifie la page on rafraichit */
  useEffect(() => {
      if(state?.page)
          dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
  }, [state.page])



  /* Lorsque les états skip et limit ont changé, on rafraichit */
  useEffect(() => {
      if (state.total > 0)
          searchHandler()
  }, [state.filters.skip, state.filters.limit])



  /* On récupère les rôles */
  useEffect(() => {
    if (errorRolesDefinitions) {
      console.error("admin/users/index::error", errorRolesDefinitions);
      if (isAxiosError(errorRolesDefinitions)) {
        dispatch({
          type: ACTIONS.SET_ERROR_MESSAGE,
          payload: "Impossible de récupérer les rôles : " + axiosErrorToStringError(errorRolesDefinitions),
        });
      } else {
        dispatch({
          type: ACTIONS.SET_ERROR_MESSAGE,
          payload: "Impossible de récupérer les rôles : " + errorRolesDefinitions?.message || "Erreur inconnue",
        });
      }
    }
  }, [errorRolesDefinitions]);



  /* Gestion du clique d'activation/désactivation d'un utilisateur */
  const activeHandler = async (user) => {
    dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

    try {
      await adminUpdateUser(user.id, { enabled: !user.enabled });
      searchHandler();
    } catch (error) {
      console.error("admin/users/index::activeHandler::error", error);
      if (isAxiosError(error)) {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
      }
    } finally {
      dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
    }
  };



  /* Gestion du double clique de suppression d'un utilisateur */
  const deleteUserHandler = async (user) => {
    dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

    try {
      await adminDeleteUser(user.id);
      searchHandler();
    } catch (error) {
      console.error('admin/users/index::deleteUserHandler::error', error)
      if (isAxiosError(error)) {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
      }
    } finally {
      dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
    }
  };



  /* Gestion du bouton de recherche */
  const searchHandler = async () => {
    const filters = state.filters;
    dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
    dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

    try {
      const [users, total] = await adminGetUsers(filters).withTotal();
      dispatch({ type: ACTIONS.SET_USERS, payload: users });
      dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
      dispatch({ type: ACTIONS.SET_TOTAL, payload: total });
    } catch (error) {
      console.error("admin/users/index::searchHandler", error);
      if (isAxiosError(error)) {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
      } else {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
      }
    } 
  };



  /* Gestion du bouton de nettoyage de la recherche */
  const clearFiltersHandler = () => {
    dispatch({ type: ACTIONS.SET_FILTERS, payload: initialState.filters });
  }




  return (
    <>
      {/* Filtres de recherche */}

      <h2 className="text-secondary text-lg flex-none content-center">Filtres de recherche</h2>
      <div className="flex flex-wrap w-full justify-start gap-4">
        <div className="flex flex-col justify-between h-12">
          <label className="text-primary text-sm" htmlFor="searchID">
            ID
          </label>
          <input
            type="text"
            id="searchID"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="ID"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "id", payload: e.target.value })}
            value={state.filters.id}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchUsername">
            Username
          </label>
          <input
            type="text"
            id="searchUsername"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Username"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "username", payload: e.target.value })}
            value={state.filters.username}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchEmail">
            Email
          </label>
          <input
            type="text"
            id="searchEmail"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Email"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "email", payload: e.target.value })}
            value={state.filters.email}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchFirstName">
            Prénom
          </label>
          <input
            type="text"
            id="searchFirstName"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Prénom"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "first_name", payload: e.target.value })}            
            value={state.filters.first_name}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchLastName">
            Nom
          </label>
          <input
            type="text"
            id="searchLastName"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Nom"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "last_name", payload: e.target.value })}
            value={state.filters.last_name}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchEnabled">
            Activé
          </label>
          <select id="searchEnabled" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "enabled", payload: e.target.value })}
            value={state.filters.enabled}
          >
            <option value="">-</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchType">
            Type de rôle
          </label>
          <select id="searchType" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "role_type", payload: e.target.value })}
            value={state.filters.role_type}
          >
            {isLoadingRolesDefinitions || isValidatingRolesDefinitions ? (
              <option value="" className="text-gray-500">
                Chargement ...
              </option>
            ) : (
              <>
                <option value="">-</option>
                {rolesDefinitions &&
                  Object.keys(rolesDefinitions).map((role, i) => {
                    return (
                      <option key={i} value={role}>
                        {role}
                      </option>
                    );
                  })}
              </>
            )}
          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchSkill">
            Métier
          </label>
          <select id="searchSkill" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "skill_id", payload: e.target.value })}
            value={state.filters.skill_id}
          >
            
            <>
              <option value="">-</option>
              {skills?.map((skill, i) => {
                return (
                  <option key={i} value={skill.id}>
                    {skill.code} - {skill.name}
                  </option>
                );
              })}
            </>

          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchCompetition">
            Compétition
          </label>
          <select id="searchCompetition" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "competition_id", payload: e.target.value })}
            value={state.filters.competition_id}
          >
            
            <>
              <option value="">-</option>
              {competitions?.map((competition, i) => {
                return (
                  <option key={i} value={competition.id}>
                    {competition.code} - {competition.name}
                  </option>
                );
              })}
            </>

          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchRegion">
            Région
          </label>
          <select id="searchRegion" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "region_id", payload: e.target.value })}
            value={state.filters.region_id}
          >
            
            <>
              <option value="">-</option>
              {regions?.map((region, i) => {
                return (
                  <option key={i} value={region.id}>
                    {region.code}
                  </option>
                );
              })}
            </>

          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchLastSeenBefore">
            Dernière visite avant
          </label>
          <input
            type="datetime-local"
            id="searchLastSeenBefore"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Dernière visite avant"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "last_seen_before", payload: e.target.value })}
            value={state.filters.last_seen_before}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchLastSeenAfter">
            Dernière visite après
          </label>
          <input
            type="datetime-local"
            id="searchLastSeenAfter"
            className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
            placeholder="Dernière visite après"
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "last_seen_after", payload: e.target.value })}
            value={state.filters.last_seen_after}
          />
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchOnKeycloak">
            Utilisateur SSO
          </label>
          <select id="searchOnKeycloak" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "on_keycloak", payload: e.target.value })}
            value={state.filters.on_keycloak}
          >
            <option value="">-</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="searchOnInscriptionsPlateform">
            Depuis plateforme d&apos;inscription
          </label>
          <select id="searchOnInscriptionsPlateform" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "on_inscriptions_plateform", payload: e.target.value })}
            value={state.filters.on_inscriptions_plateform}
          >
            <option value="">-</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        </div>
        <div className="flex flex-col justify-between">
          <label className="text-primary text-sm" htmlFor="hidden">
            Exclus du WhoIsWho
          </label>
          <select id="hidden" className="border border-gray-300 rounded-sm py-0 text-sm" 
            onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "hidden", payload: e.target.value })}
            value={state.filters.hidden}
          >
            <option value="">-</option>
            <option value="true">Oui</option>
            <option value="false">Non</option>
          </select>
        </div>
        <div className="flex flex-col justify-end">
          <button 
            className="pb-1" onClick={() => {dispatch({ type: ACTIONS.SET_PAGE, payload: 1 });searchHandler()}}
            title="Rechercher">
            <Search size={32} className="text-primary hover:text-blue-600" />
          </button>
        </div>
        <div className="flex flex-col justify-end">
          <button 
            className="pb-1" onClick={() => clearFiltersHandler()}
            title="Nettoyer la recherche">
            <SearchX size={20} className="text-primary hover:text-red-600" />
          </button>
        </div>
      </div>


      {/* Affichage de la liste des utilisateurs */}

      <div className="flex flex-wrap w-full h-14 justify-between mb-3 ">
        <div className="flex content-center items-center gap-2">
          <h2 className="text-secondary text-lg flex-none content-center">Liste des utilisateurs</h2>
          <select className="border-1 border-gray-400 py-1 text-sm text-secondary h-10"
            onChange={(e) => dispatch({ type: ACTIONS.SET_PAGE_SIZE, payload: e.target.value })} 
            value={state.page_size}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div className="flex flex-wrap content-center">
          {state.errorMessage && (
            <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
          )}
          {state.isLoading ? <ScaleLoader height={20} color={"#696969"} cssOverride={{alignSelf: "center"}} /> : null}
          <Link
            to="/admin/users/add"
            state={{ from: location}}
            className="ml-4 border rounded-lg h-10 w-10 border-secondary border-dashed hover:bg-blue-100 content-center"
          >
            <Plus className="mx-auto" />
          </Link>
        </div>
      </div>
      <table className="table-auto w-full border border-separate rounded-xl border-blue-500 border-spacing-0">
        <thead className="bg-blue-200">
          <tr className="h-10">
            <th className="px-4 py-2 text-left rounded-tl-xl">Nom</th>
            <th className="px-4 py-2 text-center">Email</th>
            <th className="px-4 py-2 text-center">Activé</th>
            <th className="px-4 py-2 text-center">Rôles</th>
            <th className="px-4 py-2 text-center rounded-tr-xl">Autres informations</th>
          </tr>
        </thead>
        <tbody>
          {state.users?.length > 0 ? (
            state.users.map((user, i) => {
              return (
                <tr
                  key={user.id}
                  className={`h-12 ${i % 2 === 0 && "bg-blue-100"} hover:bg-sky-200 ${
                    user.enabled === false && "font-italic text-gray-CCC"
                  }`}
                >
                  <th className={`text-left flex-auto flex-wrap px-1 ${i === state.users.length - 1 && "rounded-bl-xl"}`}>
                    <div className="text-secondary text-xs flex items-center gap-2">
                      <span className="content-center">ID:{user.id} / USERNAME:{user.username}</span> <button
                        onDoubleClick={() => deleteUserHandler(user)}
                        title="Double-cliquez pour supprimer l'utilisateur"
                      ><Trash size={10} className="text-red-500 hover:text-red-700" />
                      </button>
                    </div>
                    <div className="text-primary text-base flex items-center">
                      <Link to={"/admin/users/" + user.id} state={{ from: location}}>
                        <Pencil size={12} className="flex content-center mr-2" />
                      </Link>

                      <span className="flex content-center font-bold items-center gap-1">
                        {user.first_name} {user.last_name} 
                        <Circle size={12} className={` ${user.is_online ? "text-green-700" : "text-red-700"} content-center`} 
                          title={user.is_online ? "Utilisateur connecté" : "Utilisateur déconnecté"}
                        />
                      </span>
                    </div>
                  </th>
                  <td className="text-center flex-auto">
                    <span className="text-secondary text-sm">{user.email}</span>
                  </td>
                  <td className="text-center flex-auto">
                    <button
                      onClick={() => activeHandler(user)}
                      className="mx-auto flex items-center"
                      title={user.enabled ? "Désactiver l'utilisateur" : "Activer l'utilisateur"}
                    >
                      {user.enabled ? (
                        <ToggleLeft size={24} className="text-green-700 mx-auto" />
                      ) : (
                        <ToggleRight size={24} className="text-red-700 mx-auto" />
                      )}
                    </button>
                  </td>
                  <td className={`px-4 py-2 text-center flex-auto`}>
                    <div className="flex flex-wrap w-full gap-1 justify-center">
                      {user?.roles?.map((role, i) => {
                        const borderColor =
                          role.rights.filter((right) => right.includes("_admin")).length > 0
                            ? "border-red-500"
                            : "border-blue-500";

                        return (
                          <span
                            key={i}
                            className={`text-nowrap text-xs border rounded-md bg-slate-100 px-2 py-1 ${borderColor}`}
                          >
                            {role.name}
                            {role.region && <span className="text-secondary"> ({role.region.code})</span>}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className={`flex-auto px-4 py-2 text-center ${i === state.users.length - 1 && "rounded-br-xl"}`}>
                    <div className="flex flex-col items-start text-xs">
                      <span>Dernière visite : {user.last_seen ? dayjs.utc(user.last_seen).local().format('LLL') : "N/A"}</span>
                      <span className="flex items-center justify-center">Keycloak : {user.on_keycloak ? <Check size={12} className="text-green-700 content-center mx-1" /> : <X size={12} className="text-red-700 content-center mx-1" />}</span>
                      <span className="flex items-center justify-center">Plateforme d&apos;inscription : {user.on_inscriptions_plateform ? <Check size={12} className="text-green-700 content-center mx-1" /> : <X size={12} className="text-red-700 content-center mx-1" />}</span>
                      <span className="flex items-center justify-center">Exclus du WhoIsWho : {user.hidden ? <Check size={12} className="text-green-700 content-center mx-1" /> : <X size={12} className="text-red-700 content-center mx-1" />}</span>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : state.users ? (
            <tr>
              <td colSpan={5} className="text-center text-secondary py-3">
                Aucun utilisateur trouvé
              </td>
            </tr>
          ) : (
            <tr>
              <td colSpan={5} className="text-center text-secondary py-3">
                Lancez la recherche pour afficher les utilisateurs
              </td>
            </tr>
          )}
        </tbody>
      </table>
      { state.total > 0 &&
        <div className="w-full flex flex-row gap-5 justify-end mt-4">
                              
          <div className="flex flex-row gap-2 items-center border-collapse">
              
              <button 
                  className="text-base border border-gray-400 px-2 rounded-s-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                  disabled={state.page <= 1 || state.isLoading}
                  onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page - 1 })}}
              >
                  <ChevronLeft size={24} />
              </button>

              <span className="text-secondary text-base border border-gray-400 px-2 border-collapse">
                  {state.page} / {Math.ceil(state.total / state.page_size)}
              </span>

              <button 
                  className="text-base border border-gray-400 px-2 rounded-e-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                  disabled={state.page >= Math.ceil(state.total / state.page_size) || state.isLoading}
                  onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page + 1 })}}
              >
                  <ChevronRight size={24} />
              </button>

          </div>
      </div>
      }
    </>
  );
}
